import { supabase } from '../lib/supabase';

export const ratingService = {
  /**
   * Submit a rating for a user after a completed negotiation
   */
  async submitRating(negotiationId, ratedId, rating, comment = '') {
    try {
      const { data: { session } } = await supabase?.auth?.getSession();
      if (!session?.user) throw new Error('Niste prijavljeni');

      const { data, error } = await supabase
        ?.from('user_ratings')
        ?.insert({
          negotiation_id: negotiationId,
          rater_id: session?.user?.id,
          rated_id: ratedId,
          rating,
          comment: comment?.trim() || null,
        })
        ?.select('id, rating, comment, created_at')
        ?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error submitting rating:', error);
      throw error;
    }
  },

  /**
   * Check if current user has already rated for a negotiation
   */
  async hasRated(negotiationId) {
    try {
      const { data: { session } } = await supabase?.auth?.getSession();
      if (!session?.user) return false;

      const { data } = await supabase
        ?.from('user_ratings')
        ?.select('id')
        ?.eq('negotiation_id', negotiationId)
        ?.eq('rater_id', session?.user?.id)
        ?.maybeSingle();

      return !!data;
    } catch {
      return false;
    }
  },

  /**
   * Get average rating and count for a user
   */
  async getUserRatingStats(userId) {
    try {
      const { data, error } = await supabase
        ?.from('user_ratings')
        ?.select('rating')
        ?.eq('rated_id', userId);

      if (error) throw error;

      const ratings = data || [];
      if (ratings?.length === 0) return { average: null, count: 0 };

      const sum = ratings?.reduce((acc, r) => acc + r?.rating, 0);
      const average = Math.round((sum / ratings?.length) * 10) / 10;
      return { average, count: ratings?.length };
    } catch (error) {
      console.error('Error fetching user rating stats:', error);
      return { average: null, count: 0 };
    }
  },

  /**
   * Get all ratings received by a user
   */
  async getUserRatings(userId) {
    try {
      const { data, error } = await supabase
        ?.from('user_ratings')
        ?.select(`
          id, rating, comment, created_at,
          rater:user_profiles!rater_id(id, full_name, profile_picture_url)
        `)
        ?.eq('rated_id', userId)
        ?.order('created_at', { ascending: false });

      if (error) throw error;

      return (data || [])?.map(r => ({
        id: r?.id,
        rating: r?.rating,
        comment: r?.comment,
        createdAt: r?.created_at,
        raterName: r?.rater?.full_name || 'Korisnik',
        raterAvatar: r?.rater?.profile_picture_url,
      }));
    } catch (error) {
      console.error('Error fetching user ratings:', error);
      return [];
    }
  },

  /**
   * Request deal close (first party)
   */
  async requestDealClose(negotiationId) {
    try {
      const { data: { session } } = await supabase?.auth?.getSession();
      if (!session?.user) throw new Error('Niste prijavljeni');

      const { data, error } = await supabase
        ?.from('negotiations')
        ?.update({
          close_requested_by: session?.user?.id,
          close_requested_at: new Date()?.toISOString(),
          updated_at: new Date()?.toISOString(),
        })
        ?.eq('id', negotiationId)
        ?.select('id, close_requested_by, close_requested_at')
        ?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error requesting deal close:', error);
      throw error;
    }
  },

  /**
   * Confirm deal close (second party) — marks negotiation as completed
   */
  async confirmDealClose(negotiationId) {
    try {
      const { data: { session } } = await supabase?.auth?.getSession();
      if (!session?.user) throw new Error('Niste prijavljeni');

      const { data, error } = await supabase
        ?.from('negotiations')
        ?.update({
          close_confirmed_by: session?.user?.id,
          close_confirmed_at: new Date()?.toISOString(),
          status: 'completed',
          completed_at: new Date()?.toISOString(),
          updated_at: new Date()?.toISOString(),
        })
        ?.eq('id', negotiationId)
        ?.select('id, status, close_confirmed_by, close_confirmed_at')
        ?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error confirming deal close:', error);
      throw error;
    }
  },

  /**
   * Decline deal close request
   */
  async declineDealClose(negotiationId) {
    try {
      const { data, error } = await supabase
        ?.from('negotiations')
        ?.update({
          close_requested_by: null,
          close_requested_at: null,
          updated_at: new Date()?.toISOString(),
        })
        ?.eq('id', negotiationId)
        ?.select('id')
        ?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error declining deal close:', error);
      throw error;
    }
  },
};
